/**
 * Bundle Rituel — calcul dynamique + ajout panier multi-items
 * Affichage cohérent avec l'Automatic Discount Shopify -15% qui s'applique au checkout.
 */
(function () {
  'use strict';

  const FORMATTER = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.discountRate = parseFloat(this.dataset.discountRate) || 0.15;
      this.mainProductId = this.dataset.mainProductId;
      this.bundleType = this.dataset.bundleType;
      this.bundleListName = this.dataset.bundleListName;
      this.mainProductName = this.dataset.mainProductName || '';
      this.mainProductCategory = this.dataset.mainProductCategory || '';
      this.cartAddUrl = this.dataset.routesCartAdd || '/cart/add.js';

      this.mainCard = this.querySelector('[data-card="main"]');
      this.mainPriceEl = this.querySelector('[data-bind="main-price"]');
      this.mainVariantEl = this.querySelector('[data-bind="main-variant"]');
      this.mainPicker = this.querySelector('[data-bind="main-picker"]');
      this.subtotalEl = this.querySelector('[data-bind="subtotal"]');
      this.savingsEl = this.querySelector('[data-bind="savings"]');
      this.finalEl = this.querySelector('[data-bind="final-total"]');
      this.ctaBtn = this.querySelector('[data-bind="add-bundle"]');
      this.ctaLabel = this.querySelector('.bundle-builder__cta-label');
      this.errorEl = this.querySelector('[data-bind="error"]');

      this.mainPriceCents = parseInt(this.mainPriceEl.dataset.priceCents, 10) || 0;
      this.mainVariantId = this._readCurrentMainVariantId();
      this._syncingFromSelf = false;

      if (this.mainPicker) {
        this.mainPicker.addEventListener('change', () => this._onMainPickerChange());
      }
      this.querySelectorAll('[data-bind="partner-picker"]').forEach((p) => {
        p.addEventListener('change', () => this._onPartnerChange(p));
      });

      this._subscribeToVariantChange();

      this.ctaBtn.addEventListener('click', () => this.addBundleToCart());

      this.recalc();
      this._observeView();
    }

    /* ============ TRACKING ============ */

    _observeView() {
      if (this._viewedFired || typeof IntersectionObserver === 'undefined') return;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting && e.intersectionRatio >= 0.4 && !this._viewedFired) {
              this._viewedFired = true;
              this._track('bundle_viewed', this._buildContext());
              observer.disconnect();
            }
          }
        },
        { threshold: 0.4 }
      );
      observer.observe(this);
    }

    _buildItems() {
      const items = [];
      let mainVariantLabel = '';
      if (this.mainPicker) {
        const opt = this.mainPicker.options[this.mainPicker.selectedIndex];
        mainVariantLabel = (opt.textContent.split(' — ')[0] || '').trim();
      } else if (this.mainVariantEl) {
        mainVariantLabel = (this.mainVariantEl.textContent || '').trim();
      }
      items.push({
        item_id: String(this.mainVariantId || ''),
        item_name: this.mainProductName,
        item_category: this.mainProductCategory,
        item_variant: mainVariantLabel,
        price: this.mainPriceCents / 100,
        quantity: 1,
        item_list_name: this.bundleListName,
      });
      this.querySelectorAll('[data-card="partner"]').forEach((card) => {
        const picker = card.querySelector('[data-bind="partner-picker"]');
        const fixed = card.querySelector('[data-bind="partner-variant-fixed"]');
        let variantId = null;
        let priceCents = 0;
        let variantLabel = '';
        if (picker) {
          const opt = picker.options[picker.selectedIndex];
          variantId = opt.value;
          priceCents = parseInt(opt.dataset.priceCents, 10) || 0;
          variantLabel = (opt.textContent.split(' — ')[0] || '').trim();
        } else if (fixed) {
          variantId = fixed.value;
          priceCents = parseInt(fixed.dataset.priceCents, 10) || 0;
          const v = card.querySelector('.bundle-card__variant');
          if (v) variantLabel = (v.textContent || '').trim();
        }
        items.push({
          item_id: String(variantId || ''),
          item_name: card.dataset.partnerName || '',
          item_category: card.dataset.partnerCategory || '',
          item_variant: variantLabel,
          price: priceCents / 100,
          quantity: 1,
          item_list_name: this.bundleListName,
        });
      });
      return items;
    }

    _buildContext(extra) {
      const partners = this._getPartnersState();
      const itemPrices = [this.mainPriceCents, ...partners.map((p) => p.priceCents)];
      const subtotal = itemPrices.reduce((s, p) => s + p, 0);
      const totalDiscount = itemPrices.reduce((s, p) => s + Math.floor(p * this.discountRate), 0);
      const finalPrice = subtotal - totalDiscount;
      return Object.assign(
        {
          currency: 'EUR',
          value: finalPrice / 100,
          subtotal: subtotal / 100,
          discount_total: totalDiscount / 100,
          bundle_type: this.bundleType,
          bundle_list_name: this.bundleListName,
          main_product_id: this.mainProductId,
          main_variant_id: this.mainVariantId,
          items: this._buildItems(),
        },
        extra || {}
      );
    }

    _track(eventName, data) {
      try {
        if (
          window.Shopify &&
          window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function'
        ) {
          window.Shopify.analytics.publish(eventName, data);
        }
      } catch (e) {
        /* silent */
      }
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: eventName }, data));
      } catch (e) {
        /* silent */
      }
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, data);
        }
      } catch (e) {
        /* silent */
      }
    }

    _readCurrentMainVariantId() {
      const productInfo =
        document.querySelector(`product-info[data-product-id="${this.mainProductId}"]`) ||
        document.querySelector('product-info');
      if (productInfo) {
        const input = productInfo.querySelector('[name="id"]');
        if (input && input.value) return input.value;
      }
      const formInput = document.querySelector(`form[action*="/cart/add"] [name="id"]`);
      if (formInput && formInput.value) return formInput.value;
      return null;
    }

    _subscribeToVariantChange() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        return;
      }
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (this._syncingFromSelf) return;
        if (!event || !event.data || !event.data.variant) return;
        const v = event.data.variant;
        this.mainVariantId = v.id;
        this.mainPriceCents = v.price;
        this.mainPriceEl.dataset.priceCents = String(v.price);
        if (this.mainVariantEl) {
          this.mainVariantEl.textContent = (v.title || '').replace('Default Title', '').trim();
        }
        if (this.mainPicker) {
          const matchOpt = Array.from(this.mainPicker.options).find(
            (o) => parseInt(o.value, 10) === parseInt(v.id, 10)
          );
          if (matchOpt) this.mainPicker.value = matchOpt.value;
        }
        this.recalc();
      });
    }

    _onMainPickerChange() {
      const opt = this.mainPicker.options[this.mainPicker.selectedIndex];
      this.mainVariantId = opt.value;
      this.mainPriceCents = parseInt(opt.dataset.priceCents, 10) || 0;
      this.mainPriceEl.dataset.priceCents = String(this.mainPriceCents);
      this.recalc();

      this._track('bundle_variant_change', this._buildContext({ change_source: 'main' }));

      const optionValuesStr = opt.dataset.optionValues || '';
      if (optionValuesStr) {
        this._syncMainVariantSelects(optionValuesStr.split('|'));
      }
    }

    _syncMainVariantSelects(optionValues) {
      const productInfo =
        document.querySelector(`product-info[data-product-id="${this.mainProductId}"]`) ||
        document.querySelector('product-info');
      if (!productInfo) return;
      const variantSelects = productInfo.querySelector('variant-selects');
      if (!variantSelects) return;

      const optionContainers = variantSelects.querySelectorAll('.product-form__input');
      let lastTarget = null;

      optionContainers.forEach((container, idx) => {
        const targetValue = optionValues[idx];
        if (!targetValue) return;

        const select = container.querySelector('select');
        if (select) {
          const matchOpt = Array.from(select.options).find(
            (o) => o.value === targetValue || o.textContent.trim() === targetValue
          );
          if (matchOpt && select.value !== matchOpt.value) {
            const prev = select.querySelector('option[selected]');
            if (prev) prev.removeAttribute('selected');
            matchOpt.setAttribute('selected', 'selected');
            select.value = matchOpt.value;
            lastTarget = select;
          }
          return;
        }

        const radios = container.querySelectorAll('input[type="radio"]');
        radios.forEach((radio) => {
          const shouldBe = radio.value === targetValue;
          if (shouldBe && !radio.checked) {
            radio.checked = true;
            lastTarget = radio;
          } else if (!shouldBe && radio.checked) {
            radio.checked = false;
          }
        });
      });

      if (lastTarget) {
        this._syncingFromSelf = true;
        lastTarget.dispatchEvent(new Event('change', { bubbles: true }));
        // Release the guard after the event bubbling completes (microtask + macrotask)
        setTimeout(() => {
          this._syncingFromSelf = false;
        }, 50);
      }
    }

    _onPartnerChange(picker) {
      this.recalc();
      this._track('bundle_variant_change', this._buildContext({ change_source: 'partner' }));
    }

    _getPartnersState() {
      const partners = [];
      this.querySelectorAll('[data-card="partner"]').forEach((card) => {
        const picker = card.querySelector('[data-bind="partner-picker"]');
        const fixed = card.querySelector('[data-bind="partner-variant-fixed"]');
        let variantId = null;
        let priceCents = 0;
        if (picker) {
          const opt = picker.options[picker.selectedIndex];
          variantId = opt.value;
          priceCents = parseInt(opt.dataset.priceCents, 10) || 0;
        } else if (fixed) {
          variantId = fixed.value;
          priceCents = parseInt(fixed.dataset.priceCents, 10) || 0;
        }
        const priceEl = card.querySelector('[data-bind="partner-price"]');
        if (priceEl) priceEl.textContent = this._fmt(priceCents);
        partners.push({ variantId, priceCents });
      });
      return partners;
    }

    recalc() {
      this.mainPriceEl.textContent = this._fmt(this.mainPriceCents);
      const partners = this._getPartnersState();
      const itemPrices = [this.mainPriceCents, ...partners.map((p) => p.priceCents)];
      const subtotal = itemPrices.reduce((s, p) => s + p, 0);
      // Shopify applique le pourcentage et arrondit le montant de remise vers le BAS sur chaque ligne
      // (Math.floor du discount par item). Sans ce match, l'affiché et le panier divergent de 1-2c.
      const totalDiscount = itemPrices.reduce(
        (s, p) => s + Math.floor(p * this.discountRate),
        0
      );
      const finalPrice = subtotal - totalDiscount;
      this.subtotalEl.textContent = this._fmt(subtotal);
      this.savingsEl.textContent = this._fmt(totalDiscount);
      this.finalEl.textContent = this._fmt(finalPrice);
      if (this.ctaLabel) {
        this.ctaLabel.textContent = `Ajouter le pack rituel · ${this._fmt(finalPrice)}`;
      }
    }

    async addBundleToCart() {
      this._clearError();
      if (!this.mainVariantId) {
        this.mainVariantId = this._readCurrentMainVariantId();
      }
      if (!this.mainVariantId) {
        this._showError('Variante produit introuvable, recharge la page.');
        return;
      }
      const partners = this._getPartnersState();
      const items = [
        { id: parseInt(this.mainVariantId, 10), quantity: 1 },
        ...partners.map((p) => ({ id: parseInt(p.variantId, 10), quantity: 1 })),
      ];

      this._track('bundle_cta_click', this._buildContext());

      this.ctaBtn.setAttribute('aria-busy', 'true');
      this.ctaBtn.classList.add('is-loading');
      this.ctaBtn.disabled = true;

      try {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections = cart && typeof cart.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map((s) => s.id)
          : [];

        const body = {
          items,
          sections,
          sections_url: window.location.pathname,
        };

        const res = await fetch(this.cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });
        const data = await res.json();

        if (data && data.status) {
          const msg = data.description || data.message || 'Erreur lors de l\'ajout';
          this._showError(msg);
          this._track('bundle_add_error', this._buildContext({ error_message: msg, error_status: data.status }));
          return;
        }

        this._track('bundle_add_to_cart', this._buildContext());

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-builder',
            productVariantId: this.mainVariantId,
            cartData: data,
          });
        }

        if (cart && typeof cart.renderContents === 'function') {
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(this.ctaBtn);
          }
          cart.renderContents(data);
          if (cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
        }

        const originalText = this.ctaLabel ? this.ctaLabel.textContent : '';
        if (this.ctaLabel) this.ctaLabel.textContent = 'Pack ajouté ✓';
        this.ctaBtn.classList.add('is-success');
        setTimeout(() => {
          if (this.ctaLabel) this.ctaLabel.textContent = originalText;
          this.ctaBtn.classList.remove('is-success');
        }, 1800);
      } catch (e) {
        console.error('[bundle-builder] add error', e);
        this._showError('Erreur réseau, réessaie dans un instant.');
        try {
          this._track('bundle_add_error', this._buildContext({ error_message: 'network', error_status: 0 }));
        } catch (_) { /* silent */ }
      } finally {
        this.ctaBtn.removeAttribute('aria-busy');
        this.ctaBtn.classList.remove('is-loading');
        this.ctaBtn.disabled = false;
      }
    }

    _showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    _clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    _fmt(cents) {
      return FORMATTER.format((cents || 0) / 100);
    }
  }

  if (!customElements.get('bundle-builder')) {
    customElements.define('bundle-builder', BundleBuilder);
  }
})();
